import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  ClickAwayListener,
  Grid,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import Table from "components/Table";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectBaseLoads, selectCurrentRoleClientId } from "store/selectors";
import { fetchBaseLoads } from "store/appSlice";
import NewBaseLoadModal from "components/Modals/NewBaseLoad";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Popper from "@mui/material/Popper";
import { deleteBaseLoad } from "api/resources/contracts";
import ErrorIcon from "@mui/icons-material/Error";

interface BaseLoadsProps {
  setSnackbar: (snackbar: { type: string; message: string }) => void;
}

const BaseLoads = ({ setSnackbar }: BaseLoadsProps) => {
  const [openModal, setOpenModal] = useState(false);
  const dispatch = useAppDispatch();
  const baseLoads = useAppSelector(selectBaseLoads);
  const currentRoleClientId = useAppSelector(selectCurrentRoleClientId);

  useEffect(() => {
    if (currentRoleClientId) {
      dispatch(fetchBaseLoads(currentRoleClientId));
    }
  }, [dispatch, currentRoleClientId]);

  const handleDeleteBaseLoad = (baseLoadId: number) => {
    deleteBaseLoad(baseLoadId).then(() => {
      dispatch(fetchBaseLoads(currentRoleClientId));
      setOpenModal(false);
      setSnackbar({
        type: "info",
        message: "Base load removed",
      });
    });
  };

  const columns = [
    {
      accessorKey: "startDate",
      header: "Start date",
      cell: (row: any) => {
        return dayjs(row.row.original.startDate).format("DD.MM.YYYY");
      },
    },
    {
      accessorKey: "endDate",
      header: "End date",
      cell: (row: any) => {
        return dayjs(row.row.original.endDate).format("DD.MM.YYYY");
      },
    },
    {
      accessorKey: "operator",
      header: "Operator",
      cell: (row: any) => {
        return row.row.original.operator.name;
      },
    },
    {
      accessorKey: "quantity",
      header: "Capacity (MW)",
      meta: { isNumeric: true },
    },
    {
      accessorKey: "actions",
      header: "",
      meta: { isNumeric: true },
      cell: (row: any) => {
        const [open, setOpen] = React.useState(false);
        const anchorRef = React.useRef<HTMLButtonElement>(null);

        const handleToggle = () => {
          setOpen((prevOpen) => !prevOpen);
        };

        const handleClose = (event: Event | React.SyntheticEvent) => {
          if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
          ) {
            return;
          }

          setOpen(false);
        };

        function handleListKeyDown(event: React.KeyboardEvent) {
          if (event.key === "Tab") {
            event.preventDefault();
            setOpen(false);
          } else if (event.key === "Escape") {
            setOpen(false);
          }
        }

        // return focus to the button when we transitioned from !open -> open
        const prevOpen = useRef(open);
        useEffect(() => {
          if (prevOpen.current === true && open === false) {
            anchorRef.current!.focus();
          }

          prevOpen.current = open;
        }, [open]);

        return (
          <div>
            <IconButton
              ref={anchorRef}
              id="composition-button"
              aria-controls={open ? "composition-menu" : undefined}
              aria-expanded={open ? "true" : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              placement="bottom"
              transition
              disablePortal
              sx={{ zIndex: 1 }}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper>
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList
                        autoFocusItem={open}
                        id="composition-menu"
                        aria-labelledby="composition-button"
                        onKeyDown={handleListKeyDown}
                      >
                        <MenuItem
                          onClick={() =>
                            handleDeleteBaseLoad(row.row.original.id)
                          }
                        >
                          Remove
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </div>
        );
      },
    },
  ];

  return (
    <Box>
      <Grid container columnSpacing={8} sx={{ mt: "1rem" }}>
        <Grid item sm={6}>
          <Button variant="contained" onClick={() => setOpenModal(true)}>
            Add base load
          </Button>
          {!!baseLoads?.length && (
            <Box>
              <Table
                data={baseLoads}
                columns={columns}
                isFetching={!baseLoads}
                pageCount={1}
                dense
              />
            </Box>
          )}
        </Grid>
        <Grid item sm={6}>
          <Typography component="p" sx={{ mb: 1 }}>
            Please enter your active base load contracts.
          </Typography>
          <Typography component="p" sx={{ mb: 4 }}>
            The base load contracts you enter here are taken into account based
            on their start and end dates, not the selected date for the supply
            plan.
          </Typography>
          <Typography
            component="p"
            sx={{
              fontWeight: 500,
              fontSize: "18px",
              mb: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            <ErrorIcon color="primary" sx={{ mr: 1 }} />
            <strong>Important:</strong>
          </Typography>
          <Typography component="p">
            Please enter your electricity values carefully:
          </Typography>
          <ul>
            <li>
              Use <strong>negative numbers (-)</strong> to indicate{" "}
              <strong>production</strong> (when you generate electricity).
            </li>
            <li>
              Use <strong>positive numbers (+)</strong> to indicate{" "}
              <strong>consumption</strong> (when you use electricity).
            </li>
          </ul>
          <Typography component="p" sx={{ mt: 1 }}>
            For example:
          </Typography>
          <ul>
            <li>-150 means you're producing at 150 kW capacity</li>
            <li>200 means you're consuming at 200 kW capacity</li>
          </ul>
          <Typography component="p" sx={{ mt: 1 }}>
            Entering the wrong sign may result in incorrect calculations.
          </Typography>
        </Grid>
      </Grid>
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <NewBaseLoadModal
          setOpenModal={setOpenModal}
          clientId={currentRoleClientId}
          setSnackbar={setSnackbar}
        />
      </Modal>
    </Box>
  );
};

export default BaseLoads;
